// @ts-strict-ignore
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LoadingState, loadingSelector } from "../../../common/state/slice/appStateSlice";
import { Input } from "src/common/components/Input";
import type { SearchableDropdownOptionGroup } from "src/common/components/SearchableDropdown/SearchableDropdown";
import SearchableDropdown from "src/common/components/SearchableDropdown/SearchableDropdown";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import TextArea from "src/common/components/Input/TextArea";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import InformationFrame from "src/common/components/InfomationFrame/InformationFrame";
import Spinner from "src/common/components/Loader/Spinner";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getCurrentUser, updateUser } from "src/features/profile/state/profileSlice";
import { getCurrentModalStateSlider } from "src/common/state/slice/modal/modalSlice";
import {
  generateVoiceOver,
  getElevenlabsVoices,
  getPreferredElevenLabsVoice,
  getTtsPreview,
  mediaSelector,
  resetTtsPreview,
  uploadMultipart,
} from "../state/gallerySlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import type { RootState } from "src/common/state/store";
import { fromFileReturnMD5AndSize } from "../state/uploadUtil";
import { MediaGenerationInfoParagraph } from "./MediaGenerationInfoParagraph";
import { GENERATE_MEDIA_PANEL_COMPONENT } from "./GenerateMediaPanel";
import type { IMedia } from "src/model/unityObject";
import RangeSelector from "../../../common/components/RangeSelector/RangeSelector";
import Tooltip from "@mui/material/Tooltip";
import { InformationCircleIcon } from "@heroicons/react/outline";
import { trackEvent } from "src/trackEvent";

type Props = {
  closeSliderAnimation: () => any;
};

const MAX_CHAR_NB = 1500;

const LabelWithTooltip = ({
  label,
  tooltip,
  value,
}: {
  label: string;
  tooltip: string;
  value?: string;
}) => {
  return (
    <div className="flex items-center gap-2 mb-1">
      <label className="text-sm font-medium text-gray-700">
        {label}
        {value && ` : ${value}`}
      </label>
      <Tooltip title={tooltip} arrow placement="top">
        <div className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0">
          <InformationCircleIcon className="w-full h-full" />
        </div>
      </Tooltip>
    </div>
  );
};

export const GenerateTtsPanel = ({ closeSliderAnimation }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(getCurrentUser);
  const openSliderId = useAppSelector(getCurrentModalStateSlider);
  const ttsPreview = useAppSelector(getTtsPreview);
  const loading = useAppSelector((state: RootState) =>
    loadingSelector(state, GENERATE_MEDIA_PANEL_COMPONENT),
  );

  const [isFormEnabled, setIsFormEnabled] = useState(true);
  const [isTtsPreviewPreparingToBeDisplayed, setIsPreviewPreparingToBeDisplayed] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [mediaNameToEdit, setMediaNameToEdit] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [nameUnique, setNameUnique] = useState(true);
  const medias = useAppSelector(mediaSelector);

  const elevenLabsVoices = useAppSelector(getElevenlabsVoices);

  const defaultTtsVoice = useAppSelector(getPreferredElevenLabsVoice);
  const [voice, setVoice] = useState(defaultTtsVoice);
  const [genderFilter, setGenderFilter] = useState<"all" | "male" | "female">("all");
  const [languageFilter, setLanguageFilter] = useState<"all" | "fr" | "en" | "es">("all");
  const [providerFilter, setProviderFilter] = useState<"all" | "elevenLabs" | "google">("all");
  const [voiceSettings, setVoiceSettings] = useState({
    stability: 0.5,
    similarity_boost: 0.5,
  });

  const providerLabels: Record<string, string> = {
    elevenLabs: "ElevenLabs",
    google: "Google",
  };

  const voiceOptionGroups = useMemo(() => {
    const filtered = elevenLabsVoices.filter((v) => {
      if (genderFilter !== "all" && v.gender !== genderFilter) return false;
      if (languageFilter !== "all" && v.language !== languageFilter) return false;
      if (providerFilter !== "all" && v.tts_provider !== providerFilter) return false;
      return true;
    });

    const groups: Record<string, SearchableDropdownOptionGroup> = {};
    filtered.forEach((v) => {
      const provider = v.tts_provider;
      if (!groups[provider]) {
        groups[provider] = {
          label: providerLabels[provider] || provider,
          options: [],
        };
      }
      const localizedGender = t(`general.${v.gender}`, {
        defaultValue: t("general.unknown"),
      });
      groups[provider].options.push({
        id: v.id,
        name: `${v.name} (${localizedGender})`,
        ...(v.is_community_voice && {
          infoTooltip: t("pages.gallery.generateVoicePanel.communityVoiceTooltip"),
        }),
      });
    });

    return Object.values(groups);
  }, [elevenLabsVoices, genderFilter, languageFilter, providerFilter, t]);

  const allFilteredOptions = useMemo(
    () => voiceOptionGroups.flatMap((g) => g.options),
    [voiceOptionGroups],
  );

  // Reset selected voice when filters remove the current selection
  useEffect(() => {
    if (allFilteredOptions.length > 0 && !allFilteredOptions.find((o) => o.id === voice.voiceId)) {
      const firstOption = allFilteredOptions[0];
      const matchingVoice = elevenLabsVoices.find((v) => v.id === firstOption.id);
      if (matchingVoice) {
        setVoice({ voiceId: matchingVoice.id, ttsProvider: matchingVoice.tts_provider });
      }
    }
  }, [allFilteredOptions]);

  // Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
    watch,
  } = useForm<{ textToGenerate: string }>({
    defaultValues: { textToGenerate: "" },
  });

  const textValue = watch("textToGenerate") || "";
  const textLen = textValue.length;

  const getErrorText = () => {
    switch (errors.textToGenerate?.type) {
      case "maxLength":
        return t("pages.gallery.generateMediaPanel.aboveMaxCharacters");
      case "isNotEmpty":
        return t("pages.gallery.generateVoicePanel.emptyText");
      default:
        return "";
    }
  };

  const resetTts = () => {
    setIsFormEnabled(true);
    reset({ textToGenerate: "" });
    setMediaNameToEdit("");
    dispatch(resetTtsPreview());
    setVoice(defaultTtsVoice);
    setGenderFilter("all");
    setLanguageFilter("all");
    setProviderFilter("all");
  };

  const tts = async () => {
    const nameNotAvailable = medias.find((m: IMedia) => m.name === mediaNameToEdit);

    if (nameNotAvailable) {
      setNameUnique(false);
      return;
    } else {
      setNameUnique(true);
    }

    trackEvent("tts-generate");
    setIsFormEnabled(false);
    setErrorMessage("");

    await dispatch(
      generateVoiceOver({
        voice: voice.voiceId,
        ttsProvider: voice.ttsProvider,
        text: getValues("textToGenerate"),
        componentId: GENERATE_MEDIA_PANEL_COMPONENT,
        language: elevenLabsVoices.find((elt) => elt.id === voice.voiceId)?.language,
        voiceSettings: voice.ttsProvider === "elevenLabs" ? voiceSettings : undefined,
      }),
    )
      .unwrap()
      .then(() => {
        setIsAlertVisible(true);
        // Save voice preference AFTER preview is displayed to avoid re-triggering
        // the App.tsx useEffect([user]) which re-dispatches fetchElevenlabsVoices
        // with the same GENERATE_MEDIA_PANEL_COMPONENT componentId, causing a second spinner.
        void dispatch(
          updateUser({
            ...currentUser,
            preferences: JSON.stringify({
              favoriteVoiceId: voice.voiceId,
              favoriteVoiceTTSProvider: voice.ttsProvider,
            }),
          }),
        );
      })
      .catch(() => {
        setIsFormEnabled(true);
        setErrorMessage(t("error.ttsGenerationError"));
      });
  };

  useEffect(() => {
    resetTts();
  }, [openSliderId]);

  useEffect(() => {
    if (ttsPreview) {
      setIsPreviewPreparingToBeDisplayed(false);
      const textToGenerate = getValues("textToGenerate");
      const sanitizedText = textToGenerate.replace(/\s+/g, " ").trim();
      const defaultName = sanitizedText.slice(0, 29).trimEnd();
      setMediaNameToEdit(defaultName);

      setTimeout(() => {
        const inputElement = document.querySelector("input[name='name']") as HTMLInputElement;
        if (inputElement) inputElement.select();
      }, 0);
    }
  }, [ttsPreview]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {isAlertVisible && (
        <InformationFrame
          className="w-[90%] px-6 py-6 mx-auto absolute top-[-20px] transition-opacity z-[1000]"
          style="success"
          onClose={() => setIsAlertVisible(false)}
          withTimeout
          showCloseButton
        >
          <div>{t("pages.gallery.generateVoicePanel.success")}</div>
        </InformationFrame>
      )}
      {errorMessage && (
        <InformationFrame
          className="w-[90%] px-6 py-6 mx-auto absolute top-[-20px] transition-opacity z-[1000]"
          style="danger"
          onClose={() => setErrorMessage("")}
          showCloseButton
        >
          <div>{errorMessage}</div>
        </InformationFrame>
      )}

      <h3 className="font-medium text-lg mb-2 mt-2">
        {t("pages.gallery.generateVoicePanel.title")}
      </h3>

      <form onSubmit={handleSubmit(tts)} className="flex flex-col flex-1 min-h-0">
        {ttsPreview && (
          <>
            <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>
            <Input
              className="mb-4"
              name="name"
              type="text"
              maxLength={30}
              value={mediaNameToEdit}
              placeholder={t("general.fileName")}
              onChange={(e) => setMediaNameToEdit(e.target.value)}
              required
              disabled={loading === LoadingState.Loading}
            />
            <div className={`text-red-600 ${nameUnique ? "invisible" : ""}`}>
              {t("general.mediaNameAlreadyUsed")}
            </div>
          </>
        )}

        {!ttsPreview && (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14 shrink-0">
                  {t("pages.gallery.generateVoicePanel.filterLanguage")}
                </span>
                <SegmentedControl<"all" | "fr" | "en" | "es">
                  options={[
                    { value: "all", label: t("general.all") },
                    { value: "fr", label: "FR" },
                    { value: "en", label: "EN" },
                    { value: "es", label: "ES" },
                  ]}
                  value={languageFilter}
                  onChange={setLanguageFilter}
                  size="sm"
                  variant="outlined"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14 shrink-0">
                  {t("pages.gallery.generateVoicePanel.filterGender")}
                </span>
                <SegmentedControl<"all" | "male" | "female">
                  options={[
                    { value: "all", label: t("general.all") },
                    { value: "male", label: t("general.male") },
                    { value: "female", label: t("general.female") },
                  ]}
                  value={genderFilter}
                  onChange={setGenderFilter}
                  size="sm"
                  variant="outlined"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14 shrink-0">
                  {t("pages.gallery.generateVoicePanel.filterSource")}
                </span>
                <SegmentedControl<"all" | "elevenLabs" | "google">
                  options={[
                    { value: "all", label: t("general.all") },
                    { value: "elevenLabs", label: providerLabels.elevenLabs },
                    { value: "google", label: providerLabels.google },
                  ]}
                  value={providerFilter}
                  onChange={setProviderFilter}
                  size="sm"
                  variant="outlined"
                />
              </div>
            </div>

            <div>
              <label className="block font-medium text-gray-700 mt-3">
                {`${t("pages.gallery.generateVoicePanel.voice")} (${allFilteredOptions.length})`}
              </label>
              <SearchableDropdown
                options={allFilteredOptions}
                optionGroups={voiceOptionGroups.length > 1 ? voiceOptionGroups : undefined}
                selectedId={voice.voiceId}
                onSelect={(selectedId) => {
                  const matchingVoice = elevenLabsVoices.find((v) => v.id === selectedId);
                  if (matchingVoice) {
                    setVoice({
                      voiceId: matchingVoice.id,
                      ttsProvider: matchingVoice.tts_provider,
                    });
                  }
                }}
                searchPlaceholder={t("pages.gallery.generateVoicePanel.searchVoice")}
                noItemFound={t("pages.gallery.generateVoicePanel.noVoiceFound")}
                listMaxHeightClassName="max-h-96"
              />
            </div>

            {voice.ttsProvider === "elevenLabs" && (
              <div className="mt-3 space-y-3">
                <div>
                  <LabelWithTooltip
                    label={t("pages.gallery.generateVoicePanel.stability")}
                    tooltip={t("pages.gallery.generateVoicePanel.stabilityTooltip")}
                    value={`${Math.round(voiceSettings.stability * 100)}%`}
                  />
                  <RangeSelector
                    value={voiceSettings.stability}
                    setValue={(val: number) =>
                      setVoiceSettings({ ...voiceSettings, stability: val })
                    }
                    disabled={false}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    printValue={false}
                  />
                </div>

                <div>
                  <LabelWithTooltip
                    label={t("pages.gallery.generateVoicePanel.similarityBoost")}
                    tooltip={t("pages.gallery.generateVoicePanel.similarityBoostTooltip")}
                    value={`${Math.round(voiceSettings.similarity_boost * 100)}%`}
                  />
                  <RangeSelector
                    value={voiceSettings.similarity_boost}
                    setValue={(val: number) =>
                      setVoiceSettings({ ...voiceSettings, similarity_boost: val })
                    }
                    disabled={false}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    printValue={false}
                  />
                </div>
              </div>
            )}
          </>
        )}

        <div className={`flex flex-col ${!ttsPreview ? "flex-1 min-h-0" : ""}`}>
          <IsLoading
            componentId={GENERATE_MEDIA_PANEL_COMPONENT}
            spinnerPlaceholder
            showSuccess={false}
            spinnerStyle={!ttsPreview ? { minHeight: "10rem" } : undefined}
          >
            {!ttsPreview ? (
              isTtsPreviewPreparingToBeDisplayed ? (
                <div className="flex items-center justify-center relative h-full">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="block font-medium text-gray-700">
                      {t("general.textVoice")}
                    </span>
                    <Tooltip
                      title={t("pages.gallery.generateVoicePanel.disclaimer")}
                      arrow
                      placement="top"
                    >
                      <div className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0">
                        <InformationCircleIcon className="w-full h-full" />
                      </div>
                    </Tooltip>
                  </div>
                  <TextArea
                    isResizable={false}
                    className="bg-white w-full h-40 mt-2"
                    placeholder={t("pages.gallery.generateVoicePanel.textInputPlaceholder")}
                    maxLength={MAX_CHAR_NB}
                    registerFct={() =>
                      register("textToGenerate", {
                        maxLength: MAX_CHAR_NB,
                        validate: { isNotEmpty: (value) => value.trim().length > 0 },
                      })
                    }
                  />

                  <div
                    style={{
                      color: textLen >= MAX_CHAR_NB ? "red" : "gray",
                      marginTop: "0.5rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    {textLen}/{MAX_CHAR_NB}
                  </div>

                  {errors.textToGenerate && (
                    <span className="text-red-600 text-sm mt-2">{getErrorText()}</span>
                  )}
                </>
              )
            ) : (
              <div className="mt-4">
                <audio id="tts-preview" className="w-full" controls autoPlay>
                  <source src={ttsPreview.url} type="audio/mpeg" />
                </audio>
              </div>
            )}

            <div className="mt-5" key={JSON.stringify(ttsPreview)}>
              {!ttsPreview ? (
                <button
                  disabled={!isFormEnabled}
                  className={`btn-primary-fill mr-3 mt-2 ${
                    !isFormEnabled ? "cursor-not-allowed" : ""
                  }`}
                  type="submit"
                >
                  {t("general.generate")}
                </button>
              ) : (
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    className="btn-primary-outline mr-3 mt-2"
                    onClick={resetTts}
                  >
                    {t("general.cancel")}
                  </button>

                  <button
                    type="button"
                    className="btn-primary-fill mr-3 mt-2"
                    disabled={!mediaNameToEdit || isSaving}
                    onClick={async () => {
                      if (!ttsPreview || isSaving) return;

                      setIsSaving(true);
                      try {
                        const nameNotAvailable = medias.find(
                          (m: IMedia) => m.name === mediaNameToEdit,
                        );

                        if (nameNotAvailable) {
                          setNameUnique(false);
                          setIsSaving(false);
                          return;
                        } else {
                          setNameUnique(true);
                        }

                        const response = await fetch(ttsPreview.url);
                        if (!response.ok) throw new Error(`Blob fetch failed: ${response.status}`);
                        const blob = await response.blob();
                        const file = new File([blob], `${mediaNameToEdit || "audio_preview"}.mp3`, {
                          type: "audio/mpeg",
                        });

                        const { MD5 } = await fromFileReturnMD5AndSize(file);

                        const uploadParams = {
                          file,
                          orgId: currentUser.organization.id,
                          name: mediaNameToEdit || "audio_preview",
                          media_360_tag: "other",
                          MD5,
                          metadata: { initialPrompt: getValues("textToGenerate") },
                        };

                        await dispatch(uploadMultipart(uploadParams))
                          .then(() => {
                            dispatch(
                              setAlert({
                                type: "success",
                                msg: t("pages.gallery.generateVoicePanel.uploadSuccess"),
                              }),
                            );
                            resetTts();
                            closeSliderAnimation();
                          })
                          .catch((err) => {
                            console.error("Upload error:", err);
                          });
                      } catch (err) {
                        console.error("Error handling file upload:", err);
                        dispatch(
                          setAlert({
                            type: "error",
                            msg:
                              t("pages.gallery.generateVoicePanel.uploadFailure") ??
                              "Upload failed.",
                          }),
                        );
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                  >
                    {t("general.save")}
                  </button>
                </div>
              )}
            </div>
          </IsLoading>
        </div>
      </form>
      <MediaGenerationInfoParagraph
        text={t("pages.gallery.generateVoicePanel.disclaimer2")}
        customStyle={{ marginTop: "1.5rem" }}
      />
    </div>
  );
};

export default GenerateTtsPanel;
